import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, CheckCircle2, Heart, Users, Sparkles, Shield, ArrowRight, Clock, Star } from "lucide-react";
import { Link } from "react-router-dom";
import ServiceFAQ from "@/components/ServiceFAQ";
import { generateLocalBusinessSchema } from "@/lib/schemaGenerator";
import specialEventsImg from "@/assets/special-events.webp";
import specialEventsVideo from "@/assets/special-events-cinematic.mp4.asset.json";
const SpecialEvents = () => {
  const faqs = [{
    question: "How does hourly service work?",
    answer: "You book a minimum number of hours (typically 3-4 hours), and the vehicle and chauffeur remain at your disposal for that duration. You can make multiple stops, change plans, and the chauffeur waits while you enjoy your event."
  }, {
    question: "Can we make multiple stops during hourly service?",
    answer: "Yes! Hourly service is perfect for multiple stops. Visit dinner, then a concert, then drinks - all with the same vehicle and chauffeur."
  }, {
    question: "Is there a minimum booking for special events?",
    answer: "Most special event bookings have a 3-4 hour minimum, depending on the type of event and timing. Contact us for specific requirements."
  }, {
    question: "Do you provide transportation for prom and school events?",
    answer: "Yes! We provide safe, elegant transportation for prom, homecoming, and graduation. Parents appreciate our professional service and safety standards."
  }, {
    question: "Can we extend our time if the event runs late?",
    answer: "Yes, if the chauffeur is available, you can extend your service. We recommend booking extra time upfront to ensure availability."
  }];
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/special-events#service",
    "name": "Special Events Transportation San Antonio",
    "serviceType": "Special Event Transportation",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com#business"
    },
    "areaServed": {
      "@type": "City",
      "name": "San Antonio"
    },
    "description": "Premium transportation for concerts, sporting events, nights out, and special occasions. Hourly luxury car service in San Antonio.",
    "image": ["https://ricksblackcarservicesantonio.com/og-image.jpg"],
    "offers": {
      "@type": "Offer",
      "url": "https://ricksblackcarservicesantonio.com/special-events",
      "priceCurrency": "USD",
      "priceValidUntil": "2025-12-31",
      "availability": "https://schema.org/InStock"
    }
  };
  return <>
      <SEO title="Special Event Transportation San Antonio | Hourly Service" description="Premium event transportation in San Antonio for concerts, sporting events & celebrations. Hourly luxury car service 24/7. Book now!" keywords="special event transportation san antonio, concert transportation san antonio, sporting event car service, prom transportation, night out car service, hourly chauffeur san antonio" canonicalPath="/special-events" schema={schemaMarkup} />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb 
            items={[
              { name: "Services", url: "/services" },
              { name: "Special Events", url: "/services/special-events" }
            ]}
          />

          {/* Hero Section */}
          <section className="relative h-[60vh] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 z-0">
              <video
                className="w-full h-full object-cover brightness-90"
                src={specialEventsVideo.url}
                poster={specialEventsImg}
                autoPlay
                loop
                muted
                playsInline
               preload="metadata" disableRemotePlayback/>
              <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/50 to-background/70" />
              <div className="absolute inset-0 pointer-events-none [background:radial-gradient(ellipse_at_center,transparent_55%,rgba(0,0,0,0.55)_100%)]" />
            </div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-foreground mb-6">
                San Antonio Special Event Transportation: Premium Hourly Car Service
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Concerts | Sporting Events | Nights Out | Special Occasions
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now: 210-296-7409
                  </a>
                </Button>
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                    Book Your Private Executive Car Service Today
                  </a>
                </Button>
              </div>
            </div>
          </section>

          {/* Introduction */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                    Make every occasion unforgettable with premium <strong className="text-foreground">Special Event Transportation San Antonio</strong> from Rick's Black Car Service. Whether you're attending a concert at AT&T Center, a Spurs game, anniversary dinner, or celebrating a milestone, our professional <strong className="text-foreground">hourly car service</strong> ensures you arrive in style and comfort. Enjoy your event worry-free knowing you have reliable, elegant transportation throughout the evening.
                  </p>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                    Our <strong className="text-foreground">luxury event transportation</strong> provides flexible hourly service tailored to your schedule. From intimate date nights to group celebrations, we accommodate events of all sizes throughout San Antonio and surrounding areas. Let our professional chauffeur handle the driving while you focus on creating unforgettable memories.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Special Event Services */}
          <section className="py-20 bg-secondary">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground text-center mb-12">
                  Luxury Event Transportation Services in San Antonio
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card className="card-luxury">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Concert Transportation</h3>
                      <p className="text-muted-foreground">AT&T Center, Majestic Theatre, and all San Antonio venues</p>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Sporting Events</h3>
                      <p className="text-muted-foreground">Spurs games, Missions baseball, and sporting venues</p>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Anniversary & Date Nights</h3>
                      <p className="text-muted-foreground">Romantic transportation for special occasions</p>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Birthday Celebrations</h3>
                      <p className="text-muted-foreground">Make birthdays memorable with luxury service</p>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Prom & Homecoming</h3>
                      <p className="text-muted-foreground">Safe, elegant transportation for students</p>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Graduation Celebrations</h3>
                      <p className="text-muted-foreground">Commemorate achievements in style</p>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Night Out Transportation</h3>
                      <p className="text-muted-foreground">Bar hopping, club nights, and entertainment districts</p>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Theater & Shows</h3>
                      <p className="text-muted-foreground">Broadway shows, performances, and cultural events</p>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Casino Trips</h3>
                      <p className="text-muted-foreground">Transportation to Eagle Pass and other gaming destinations</p>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Holiday Events</h3>
                      <p className="text-muted-foreground">Christmas lights tours, New Year&apos;s Eve, and holiday parties</p>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Quinceañeras</h3>
                      <p className="text-muted-foreground">Elegant transportation for this special milestone</p>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Bachelor/Bachelorette Parties</h3>
                      <p className="text-muted-foreground">Fun, safe group transportation</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Popular Event Venues */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground text-center mb-6">
                  Popular San Antonio Event Venues
                </h2>
                <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
                  We provide transportation to all San Antonio venues including:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {["AT&T Center (Spurs, concerts, events)", "Alamodome (conventions, sports, concerts)", "Majestic Theatre (Broadway shows, performances)", "Tobin Center for the Performing Arts", "Pearl District (dining, entertainment)", "Southtown Arts District (galleries, restaurants, nightlife)", "St. Mary's Strip (bars, clubs, entertainment)", "The Rim (dining, entertainment)", "La Cantera (shopping, dining, entertainment)", "River Walk (restaurants, bars, attractions)", "And all San Antonio event locations"].map((venue, index) => <Card key={index} className="card-luxury">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <p className="text-foreground text-sm">{venue}</p>
                        </div>
                      </CardContent>
                    </Card>)}
                </div>
              </div>
            </div>
          </section>

          {/* Why Choose Hourly Service */}
          <section className="py-20 bg-secondary">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground text-center mb-12">
                  Why Choose Hourly Service
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[{
                  title: "Flexibility",
                  desc: "Vehicle and chauffeur available for your entire event"
                }, {
                  title: "No Rush",
                  desc: "Take your time, no need to schedule separate pickups"
                }, {
                  title: "Multiple Stops",
                  desc: "Visit multiple venues in one evening"
                }, {
                  title: "Wait Time Included",
                  desc: "Chauffeur waits while you enjoy your event"
                }, {
                  title: "Spontaneity",
                  desc: "Change plans on the fly without rebooking"
                }, {
                  title: "Group Coordination",
                  desc: "Keep everyone together with one vehicle"
                }, {
                  title: "VIP Experience",
                  desc: "Arrive and depart like a celebrity"
                }, {
                  title: "Safe Transportation",
                  desc: "Designated driver for the entire evening"
                }, {
                  title: "Stress-Free",
                  desc: "No parking hassles or navigation concerns"
                }, {
                  title: "Photo-Ready",
                  desc: "Luxury vehicle perfect for special occasion photos"
                }].map((feature, index) => <Card key={index} className="card-luxury">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <Star className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                          <div>
                            <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                            <p className="text-muted-foreground">{feature.desc}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>)}
                </div>
              </div>
            </div>
          </section>

          {/* Hourly Service Options */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground text-center mb-12">
                  Hourly Service Options
                </h2>
                <div className="space-y-6">
                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Clock className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">3-Hour Minimum</h3>
                          <p className="text-muted-foreground leading-relaxed">Perfect for dinners and short events</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Clock className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">4-6 Hours</h3>
                          <p className="text-muted-foreground leading-relaxed">Ideal for concerts, sporting events, and nights out</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Clock className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">8+ Hours</h3>
                          <p className="text-muted-foreground leading-relaxed">Comprehensive service for all-day/all-night events</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Clock className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">Custom Duration</h3>
                          <p className="text-muted-foreground leading-relaxed">Book exactly the time you need</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Clock className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">Extended Service</h3>
                          <p className="text-muted-foreground leading-relaxed">Add hours as needed during your event</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <p className="text-muted-foreground text-center mt-8">
                  All hourly service includes professional chauffeur, luxury vehicle, fuel, and standard wait time. Additional stops and extended service areas may apply.
                </p>
              </div>
            </div>
          </section>

          {/* Group Transportation */}
          <section className="py-20 bg-secondary">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground text-center mb-6">
                  Group Transportation
                </h2>
                <p className="text-lg text-muted-foreground text-center mb-12">
                  Our Luxury SUV accommodates up to 6 passengers. For larger groups, we coordinate multiple SUV trips:
                </p>
                <div className="max-w-2xl mx-auto">
                  <Card className="card-luxury">
                    <CardContent className="p-8 text-center">
                      <Users className="w-12 h-12 text-primary mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-foreground mb-2">Luxury SUV (up to 6 passengers)</h3>
                      <p className="text-muted-foreground mb-4">
                        For groups larger than 6, we can schedule multiple coordinated SUV trips to accommodate your entire party.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Safety & Responsibility */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground text-center mb-6">
                  Safety & Responsibility
                </h2>
                <p className="text-lg text-muted-foreground text-center mb-12">
                  Your safety is our priority. Our special event transportation service provides:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[{
                  title: "Professional, Licensed Chauffeurs",
                  desc: "Background checked and trained"
                }, {
                  title: "Designated Driver Service",
                  desc: "Enjoy your event responsibly"
                }, {
                  title: "24/7 Availability",
                  desc: "Early events and late nights covered"
                }, {
                  title: "Vehicle Monitoring",
                  desc: "GPS tracking for safety and coordination"
                }, {
                  title: "Insurance Coverage",
                  desc: "Fully insured for your protection"
                }, {
                  title: "Alcohol-Free Vehicles",
                  desc: "We provide transportation, not alcohol service"
                }, {
                  title: "Compliance with Laws",
                  desc: "All service follows local regulations"
                }].map((item, index) => <Card key={index} className="card-luxury">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <Shield className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                          <div>
                            <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                            <p className="text-muted-foreground">{item.desc}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>)}
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-12 bg-secondary">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <ServiceFAQ faqs={faqs} />
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-4xl font-serif font-bold text-foreground mb-6">
                  Book Your Special Event Transportation Today
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Call <a href="tel:210-296-7409" className="text-primary font-semibold hover:underline">210-296-7409</a> or email <a href="mailto:ricksblackcarservice@outlook.com" className="text-primary font-semibold hover:underline">ricksblackcarservice@outlook.com</a> for hourly service rates and availability. Make your next event unforgettable with luxury transportation from Rick&apos;s Black Car Service.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button className="btn-luxury text-xl px-10 py-7" asChild>
                    <a href="tel:+12102967409">
                      <Phone className="w-6 h-6 mr-2" />
                      Call: 210-296-7409
                    </a>
                  </Button>
                  <Button className="btn-luxury text-xl px-10 py-7" asChild>
                    <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                      Book Special Event Service <ArrowRight className="w-6 h-6 ml-2" />
                    </a>
                  </Button>
                  <Button variant="outline" className="text-xl px-10 py-7 border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                    <a href="mailto:ricksblackcarservice@outlook.com">
                      Email Us
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Instant Quote CTA */}
          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>;
};
export default SpecialEvents;